<div class="summary-shell p-2 p-lg-2">
      <section class="hero-card">
        <div class="hero-copy">
          <div class="hero-eyebrow">Bess</div>
          <h1>Today Application</h1>
          <p>Track planned versus actual daily energy and revenue for each BESS-linked application using linked battery assets, schedules, SOC logs, forecasts, and settlement data.</p>
          <div class="hero-ribbon">
            <span class="ribbon-chip">Application-wise variance</span>
            <span class="ribbon-chip">Revenue delta</span>
            <span class="ribbon-chip">Matrix-linked drilldown</span>
          </div>
        </div>
        <div class="hero-metrics">
          <div class="metric-chip">
            <lucide-icon [img]="CalendarIcon" [size]="16"></lucide-icon>
            <span>{{ selectedDateDisplayLabel() }}</span>
          </div>
          <div class="hero-icon">
            <lucide-icon [img]="BatteryChargingIcon" [size]="28"></lucide-icon>
          </div>
        </div>
      </section>

      <section class="control-card">
        <label class="control-field control-card-field">
          <span class="control-label">Date</span>
          <span class="control-helper">Application date</span>
          <div class="control-input-wrap">
            <span class="control-icon"><lucide-icon [img]="CalendarIcon" [size]="16"></lucide-icon></span>
            <input class="form-control control-input" type="date" [ngModel]="selectedDate()" (ngModelChange)="setSelectedDate($event)" />
          </div>
        </label>
        <div class="summary-kpis">
          <article class="kpi-card">
            <small>Applications</small>
            <strong>{{ selectedDateApplicationRows().length }}</strong>
            <span class="kpi-note">Applications on the selected date</span>
          </article>
          <article class="kpi-card">
            <small>Planned</small>
            <strong>{{ totalPlannedEnergy() | number:'1.0-1' }} MWh</strong>
            <span class="kpi-note">Forecast-backed application view</span>
          </article>
          <article class="kpi-card">
            <small>Actual</small>
            <strong>{{ totalActualEnergy() | number:'1.0-1' }} MWh</strong>
            <span class="kpi-note">Execution rolled up by application</span>
          </article>
          <article class="kpi-card d-none">
            <small>Impact</small>
            <strong [class.negative]="totalRevenueImpact() < 0">{{ formatLakh(totalRevenueImpact()) }}</strong>
            <span class="kpi-note">Daily revenue variance</span>
          </article>
        </div>
      </section>

      <section class="table-card d-none">
        <div class="table-head">
          <div>
            <div class="table-title">Today Application Details</div>
            <div class="table-subtitle">Show today's application, contract, counterparties, quantum, and power flow deviation.</div>
          </div>
        </div>
        <div class="table-wrap">
          <table class="performance-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Application Number</th>
                <th>Contract Number</th>
                <th>Seller</th>
                <th>Buyer</th>
                <th>Requested Quantum (MWh)</th>
                <th>Approved Quantum (MWh)</th>
                
                <th>Power Flow Deviation</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="8" class="empty-state">Loading today application data...</td></tr>
              } @else if (todayApplicationRows().length === 0) {
                <tr><td colspan="8" class="empty-state">No application data found for this view.</td></tr>
              } @else {
                @for (row of todayApplicationRows(); track row.applicationId) {
                  <tr class="data-row">
                    <td>{{ row.serialNo }}</td>
                    <td class="plant-cell">
                      <button type="button" class="plant-link" (click)="openBlockFlowModal(row, todayDate())">{{ row.applicationNumber }}</button>
                      <div class="plant-subcopy">{{ row.linkedAssets.length }} linked asset{{ row.linkedAssets.length === 1 ? '' : 's' }}</div>
                    </td>
                    <td>{{ row.contractNumber }}</td>
                    <td>{{ row.seller }}</td>
                    <td>{{ row.buyer }}</td>
                    
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.requestedQuantumMwh | number:'1.0-2' }} MWh</div>
                      
                    </td>
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.approvedQuantumMwh | number:'1.0-2' }} MWh</div>
                    </td>
                    <td class="impact-cell" [class.negative]="row.powerFlowDeviationMwh < 0">{{ formatSignedMwh(row.powerFlowDeviationMwh) }}</td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>

      <section class="table-card">
        <div class="table-head">
          <div>
            <div class="table-title">Applications for {{ selectedDateDisplayLabel() }}</div>
            <div class="table-subtitle">Show all application data for the selected date with daily totals.</div>
          </div>
        </div>
        <div class="table-wrap">
          <table class="performance-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Application Number</th>
                <th>Contract Number</th>
                <th>Seller</th>
                <th>Buyer</th>

                <th>Requested Quantum (MWh)</th>
                <th>Approved Quantum (MWh)</th>
                <th>Power Flow Deviation</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="8" class="empty-state">Loading application data for the selected date...</td></tr>
              } @else if (selectedDateApplicationRows().length === 0) {
                <tr><td colspan="8" class="empty-state">No application data found for the selected date.</td></tr>
              } @else {
                @for (row of selectedDateApplicationRows(); track row.applicationId) {
                  <tr class="data-row">
                    <td>{{ row.serialNo }}</td>
                    <td class="plant-cell">
                      <button type="button" class="plant-link" (click)="openBlockFlowModal(row, selectedDate())">{{ row.applicationNumber }}</button>
                      <div class="plant-subcopy">{{ row.linkedAssets.length }} linked asset{{ row.linkedAssets.length === 1 ? '' : 's' }}</div>
                    </td>
                    <td>{{ row.contractNumber }}</td>
                    <td>{{ row.seller }}</td>
                    <td>{{ row.buyer }}</td>
                    
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.requestedQuantumMwh | number:'1.0-2' }} MWh</div>
                    </td>
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.approvedQuantumMwh | number:'1.0-2' }} MWh</div>
                    </td>
                    <td class="impact-cell" [class.negative]="row.powerFlowDeviationMwh < 0">{{ formatSignedMwh(row.powerFlowDeviationMwh) }}</td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>

      @if (distributionModal()) {
        <div class="modal-backdrop" (click)="closeDistributionModal()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">{{ distributionModal()!.application }}</div>
                <h2>{{ distributionModal()!.item.label | titlecase }} Details</h2>
                <p>{{ distributionModal()!.item.count }} {{ distributionModal()!.item.detail }}</p>
              </div>
              <button type="button" class="close-btn" (click)="closeDistributionModal()">Close</button>
            </div>
            <div class="modal-body">
              @if (distributionModal()!.item.records.length === 0) {
                <div class="modal-empty">No detailed records available for this category on the selected date.</div>
              } @else {
                <div class="modal-table-wrap">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Block</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (record of distributionTableRows(distributionModal()!.item); track record.category + record.block + record.details) {
                        <tr>
                          <td>{{ record.category }}</td>
                          <td>{{ record.block }}</td>
                          <td>{{ record.details }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        </div>
      }

      @if (blockFlowModal()) {
        <div class="modal-backdrop" (click)="closeBlockFlowModal()">
          <div class="modal-card modal-card--xl" (click)="$event.stopPropagation()">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Application 96-Block Energy Flow</div>
                <h2>{{ blockFlowModal()!.applicationNumber }}</h2>
                <p>{{ blockFlowModal()!.seller }} -> {{ blockFlowModal()!.buyer }} · {{ blockFlowModal()!.date }}</p>
              </div>
              <button type="button" class="close-btn" (click)="closeBlockFlowModal()">Close</button>
            </div>
            <div class="modal-body">
              <div class="modal-table-wrap">
                <table class="modal-table modal-table--blocks">
                  <thead>
                    <tr>
                      <th>Contract No.</th>
                      <th>Seller</th>
                      <th>Buyer</th>
                      <th>Block</th>
                      <th>Time</th>
                      <th>Scheduled Quantum</th>
                      <th>Actual Quantum</th>
                      <th>Deviation</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (blockRow of blockFlowModal()!.blockRows; track blockRow.block) {
                      <tr>
                        <td>{{ blockFlowModal()!.contractNumber }}</td>
                        <td>{{ blockFlowModal()!.seller }}</td>
                        <td>{{ blockFlowModal()!.buyer }}</td>
                        <td>{{ blockRow.block }}</td>
                        <td>{{ blockRow.time }}</td>
                        <td>{{ blockRow.scheduledQuantumMw | number:'1.0-2' }}</td>
                        <td>{{ blockRow.actualQuantumMw | number:'1.0-2' }}</td>
                        <td [class.negative]="blockRow.deviationMw < 0">{{ formatSignedMw(blockRow.deviationMw) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
